import {z} from 'zod'
import type {FeatureCollection, MultiPolygon, Point, Polygon} from 'geojson'
import type {CategoryLookup} from '~/api/categories'
import type {Location} from '~/api/locations'

export type Region = {
  id: number
  name: string
}

export type City = {
  id: number
  name: string
  country: Country
}

export type Country = {
  id: number
  name: string
  code: string
  slug: string
  region: Region
}

export type Tree = {
  value: number
  title: string
  children: {
    value: number
    title: string
  }[]
}

export type Summary = {
  id: number
  summary: string
  location_name: string
  location_summary_type: string
  updated_at: string
}

export type IntelligenceReportPreview = {
  id: number
  title: string
  content: string
  image: Asset
  report_type: CategoryLookup
  reported_at: string
}

export type IntelligenceReport = IntelligenceReportPreview & {
  carto_embed: string | null
  risks: CategoryLookup[]
  sectors: CategoryLookup[]
  intelligence_types: CategoryLookup[]
  locations: Location[]
  kmz: Asset | null
}

export type DataPages = {
  id: number
  title: string
  commentary: string
  image: Asset
  reported_at: string
}

export type DataPage = DataPages & {
  link: string
  attribution: string
  locations: Location[]
  categories: CategoryLookup[]
}

export type Metadata = {
  prev: string | null
  next: string | null
  limit: number
  page: number
  count: number
}

export const assetTypes = [
  'unknown',
  'video',
  'document',
  'kmz',
  'image',
  'audio',
] as const

export const OIDC_PRESERVE_PARAMS = [
  'accountid',
  'namespace',
  'productid',
  'emglsauthnidpid',
  'xsid',
] as const

export const LOGIN_TYPES = ['KEYCLOAK', 'DRAGONFLY', 'OIDC'] as const
export type LoginType = (typeof LOGIN_TYPES)[number]

type AssetType = (typeof assetTypes)[number]

export type Asset = {
  id: string
  title: string
  attribution: string | null
  asset_type: AssetType
  description: string
  src: string
  tags?: string[]
  created_by: string | null
  created_at: string
  updated_at: string
}

export type BriefingSession = {
  id: number
  title: string
  description: string
  image: Asset
  audio: Asset
  published_at: string
  created_at: string
  updated_at: string
}

export type UserResponseLicense = {
  id: number
  created_at: string
  updated_at: string
  deleted_at: string | null
  subscription: {
    id: number
    product_name: string
    expired: string
    allowed_products: string[]
    allowed_regions: string[]
  }
}

export type User = {
  name: string
  email: string
  user_uuid: string
  dow_jones_user_id: string
  is_staff: boolean
  is_new: boolean
  subscribed_to_emergency_alerts?: boolean
  fe_permissions: string[]
  client_name: string
  client_uuid: string
  created_at: string
  licences: UserResponseLicense[]
  updated_at: string
  is_dow_jones_client: boolean
  privacy_accepted: boolean
  namespace: string
  plan_id: string
}

export type SessionUser = {
  jwt: string
  client_uuid: string
  user_uuid: string
  expires_in: number
  token_type: string
  refreshToken: string | undefined
  expires_on: string
  url?: string
  payload?: Record<string, string>
  loginType?: LoginType
}

export type Source = {
  name: string
  url: string
}

export type TravelRiskMonitorAlert = {
  id: number
  title: string
  description: string
  start_date: string
  end_date: string
  sources: Source[] | null
  alerts: CategoryLookup[] | null
  risks: CategoryLookup[] | null
  locations: Location[]
  map_data: FeatureCollection
}

export type FormattedTravelRiskMonitorAlert = Omit<
  TravelRiskMonitorAlert,
  'start_date' | 'end_date'
> & {
  start: Date
  end: Date
}

export type Perpetrator = {
  id: number
  name: string
  perpetrator_type?: CategoryLookup
}

export type PerpetratorLookup = {
  id: number
  name: string
}

type RiskRating = {
  id: number
  name: string
  rating: number
  summary: string
  description: string
}

export type RiskRatingDetail = {
  description: string
  id: number
  name: string
  ratings: RiskRating[]
  slug: string
}

export type RiskRatingType = {
  id: number
  name: string
  slug: string

  description: string
}

export type RiskRatings = FeatureCollection<
  Polygon | Point | MultiPolygon,
  {
    location_id: number
    name: string
    [key: string]: number | string
  }
>

export type NotificationInterest = {
  id: string
  subscribed: boolean
  name: string
}

export type NotificationCategory = {
  id: string
  title: string
  interests: NotificationInterest[]
}

export type NotificationSettings = {
  first_name: string
  last_name: string
  organisation: string
  email_type: 'html' | 'text'
  job_title?: string
  categories: NotificationCategory[]
  email_subscription_status:
    | 'unsubscribed'
    | 'subscribed'
    | 'pending'
    | 'cleaned'
    | 'N/A'
}

export type SubscriptionDetail = {
  id: number
  client_id: number
  salesforce_code: string
  number_of_licences: number
  expired: string
  allowed_products: string[]
  allowed_regions: string[]
  trial: boolean
  remaining_licences: number
  salesforce_subscription_id: string
  previous_salesforce_subscription_id: string | null
  start_date: string
  created_at: string
  updated_at: string
  user_licences: License[]
  product_type: 'API' | 'WEBSITE'
  product_name: string
}

export type Subscription = {
  allowed_products: string[]
  allowed_regions: string[]
  expired: string
  id: number
  product_name: string
}

export type License = {
  id: number
  email: string
  user_id: number
  created_at: string
  updated_at: string
  deleted_at: string | null
  status: string
  name: string
  // subscription: Subscription
}

export type Client = {
  client_uuid: string
  email: string
  name: string
  public_key: string | null
  // todo: User has a BaseSubscription,
  // maybe a BaseSubscription and FullSubscription are needed
  subscriptions: (Subscription & {
    client_id: number
    created_at: string
    number_of_licences: number
    previous_salesforce_subscription_id: null
    product_type: string
    remaining_licences: number
    salesforce_code: string
    salesforce_subscription_id: string
    start_date: string
    trial: boolean
    updated_at: string
  })[]
  created_at: string
  updated_at: string
}

export type ApiError = {
  status: string
  code: number
  message: string
  field_errors?: Record<string, string[]>
}

export type DataMapsInfo = {
  name: string
  slug: string
  description: string
  legend: {
    name: string
    rating: number
    colour: string
  }[]
}[]

export type DataMap = FeatureCollection<
  Polygon | MultiPolygon,
  {
    country: string
    [key: string]: number | string
  }
>

const defaultEnabledFeatureFlagSchema = z.preprocess(
  (value) => {
    if (typeof value !== 'string') return value

    const normalizedValue = value.trim().toLowerCase()

    return normalizedValue || undefined
  },
  z
    .enum(['true', 'false'])
    .default('true')
    .transform((value) => value === 'true'),
)

export const envVariablesSchema = z.object({
  API_URL: z.string(),
  APP_ENV: z.string(),
  COOKIE_SECRET: z.string(),
  ADMIN_URL: z.string(),
  MAGIC_LINK_SECRET: z.string(),
  KEYCLOAK_AUTH_ISSUER_DOMAIN: z.string(),
  KEYCLOAK_AUTH_CLIENT_ID: z.string(),
  KEYCLOAK_AUTH_CLIENT_SECRET: z.string(),
  KEYCLOAK_AUTH_REDIRECT_URL: z.string(),
  KEYCLOAK_AUTH_ISSUER_REALM: z.string(),
  LOGOUT_REDIRECT_URL: z.string(),
  KEYCLOAK_AUTH_URL: z.string(),
  OIDC_AUTH_CLIENT_ID: z.string(),
  OIDC_AUTH_CLIENT_SECRET: z.string(),
  OIDC_AUTH_REDIRECT_URL: z.string(),
  OIDC_AUTH_URL: z.string(),
  OIDC_AUTH_TOKEN_URL: z.string(),
  SENTRY_DSN: z.string(),
  MOBILE_APP_ID: z.string(),
  PENDO_API_KEY: z.string().default(''),
  LANDING_PAGE: z.string(),
  LOGIN_TYPE: z.enum(['KEYCLOAK', 'DRAGONFLY', 'OIDC']),
  CHAMELEON_APP_ID: z.string(),
  MAPBOX_ACCESS_TOKEN: z.string(),
  DEFAULT_ROUTE: z.string().default('/dashboard'),
  ENABLE_THREAT_EVENTS: z
    .string()
    .transform((val) => 'true' === val)
    .default('false'),
  SHOW_PRIVACY_TO_ALL_USERS: z
    .string()
    .transform((val) => 'true' === val)
    .default('false'),
  ENABLE_LEGACY_REDIRECTS: z
    .string()
    .transform((val) => 'true' === val)
    .default('false'),
  REDIRECT_TARGET_BASE: z.string().default(''),
  REDIRECT_STATUS_CODE: z
    .string()
    .transform((val) => Number(val))
    .default('302'),
  COUNTRY_DOSSIER_EDITING_ENABLED: defaultEnabledFeatureFlagSchema,
})

export const clientEnvVariablesSchema = envVariablesSchema.pick({
  APP_ENV: true,
  ADMIN_URL: true,
  SENTRY_DSN: true,
  PENDO_API_KEY: true,
  LOGIN_TYPE: true,
  CHAMELEON_APP_ID: true,
  LANDING_PAGE: true,
  MAPBOX_ACCESS_TOKEN: true,
  ENABLE_THREAT_EVENTS: true,
})

declare global {
  interface Window {
    ENV: z.infer<typeof clientEnvVariablesSchema>
  }
}

export type PublicShareDetail = IntelligenceReport | BriefingSession

// Export CSV types
export const exportJobResources = [
  'terrorism-tracker',
  'threat-events',
  'risk-scores',
] as const
export type ExportJobResource = (typeof exportJobResources)[number]

export const exportJobStatuses = [
  'queued',
  'running',
  'completed',
  'failed',
  'expired',
] as const
export type ExportJobStatus = (typeof exportJobStatuses)[number]

export type ExportCreateMode = 'auto'
export type ExportModeDecision = 'sync' | 'async'
export type ExportLocationType = 'country' | 'city'

export type TerrorismEventExportFilters = {
  q?: string
  from_date?: string
  to_date?: string
  polymorphic_type?: 'incident' | 'plot'
  location_ids?: number[]
  event_type_ids?: number[]
  sector_type_ids?: number[]
  perpetrator_ids?: number[]
  perpetrator_type_ids?: number[]
  weapon_type_ids?: number[]
  planning_stage_ids?: number[]
  delivery_method_ids?: number[]
  suicide?: boolean | 'true' | 'false'
  ambiguous?: boolean | 'true' | 'false'
  sort?: string
  order?: 'asc' | 'desc'
}

export type ThreatEventsExportFilters = {
  q?: string
  from_date?: string
  to_date?: string
  live_at?: string
  event_flags?: ('hybrid_warfare' | 'incident' | 'plot')[]
  location_ids?: number[]
  activity_ids?: number[]
  event_type_ids?: number[]
  event_vector_ids?: number[]
  instrument_ids?: number[]
  perpetrator_ids?: number[]
  perpetrator_type_ids?: number[]
  ambiguous?: boolean | 'true' | 'false'
  sort?: string
  order?: 'asc' | 'desc'
}

export type RiskRatingsExportFilters = {
  location_types?: ExportLocationType[]
  location_ids?: number[]
  risk_ids?: number[]
  reported_from_date?: string
  reported_to_date?: string
}

export type ExportCreateRequest =
  | {
      resource: 'terrorism-tracker'
      schema_version: string
      mode: ExportCreateMode
      filters: TerrorismEventExportFilters
    }
  | {
      resource: 'threat-events'
      schema_version: string
      mode: ExportCreateMode
      filters: ThreatEventsExportFilters
    }
  | {
      resource: 'risk-scores'
      schema_version: string
      mode: ExportCreateMode
      filters: RiskRatingsExportFilters
    }

export type ExportJobProgress = {
  percent: number
  completed_units: number
  total_units: number
  unit: string
  is_estimated: boolean
}

export type ExportJob = {
  job_id: string
  resource: ExportJobResource
  schema_version: string
  status: ExportJobStatus
  mode_decision: ExportModeDecision
  snapshot_at: string
  estimated_rows: number | null
  actual_rows: number | null
  truncated: boolean
  truncation_limit: number | null
  filters_used:
    | TerrorismEventExportFilters
    | ThreatEventsExportFilters
    | RiskRatingsExportFilters
  download_url: string | null
  created_at: string
  started_at: string | null
  completed_at: string | null
  expires_at: string | null
  error_code: string | null
  error_message: string | null
  progress?: ExportJobProgress | null
}

export type ExportJobResponse = {
  data: ExportJob
}

export type ExportJobListItem = {
  job_id: string
  resource: ExportJobResource
  schema_version: string
  status: ExportJobStatus
  created_at: string
  completed_at: string | null
  truncated: boolean
  filters_used:
    | TerrorismEventExportFilters
    | ThreatEventsExportFilters
    | RiskRatingsExportFilters
}

export type ExportJobListResponse = {
  data: ExportJobListItem[]
  metadata: {
    page: number
    limit: number
    count: number
  }
}

export type ExportValidationError = {
  error: {
    code: number
    status: string
    message: string
    field_errors: Record<string, string>
  }
}

export type ExportRateLimitError = {
  error: {
    code: number
    status: string
    message: string
    retry_after_seconds: number
  }
}

export interface RiskLevelConfig {
  rating: number
  color: string
  textColor: string
  name: string
}

type PostAuthResponse = {
  payload: Record<string, string>
  url: string
}
type ProhibitedUserResponse = {
  destroySession: boolean
  token: string
  message: string
}

export type AuthResult = SessionUser | ProhibitedUserResponse | PostAuthResponse
